import { summarizeFormulaComparison } from '@/md/entities/priceRes/PriceResFormulaComparisonUtils';
import { displayValueToken, tokenizeFormula } from '@/md/entities/tier/formFields/TierFormulaUtils';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const formatNumber = (value) => value.toLocaleString('en-US', { maximumFractionDigits: 2 });

// A metric chip's value, in "Show Values" mode; falls back to the raw string if it isn't numeric
const formatMetricValue = (rawValue) => {
  if (rawValue == null) return '0';
  const value = Number(rawValue);
  return Number.isNaN(value) ? rawValue : formatNumber(value);
};

// Read-only rendering of the formula, reusing the tokenizing/chip display from TierFormulaBuilder
// (minus the palette, drag-and-drop, and editing). Metric chips are tooltipped with the value used at
// eval time (sourced from vars' JSON), same as PriceResFormulaContent/PriceResFormulaCellRenderer.
export const PriceResInfoDialog = ({ data, closeDialog }) => {
  const tokens = tokenizeFormula(data.formula);
  const [showValues, setShowValues] = useState(false);

  let codeToValue = {};
  try {
    codeToValue = data.vars ? JSON.parse(data.vars) : {};
  } catch {
    codeToValue = {};
  }

  const summary = summarizeFormulaComparison(tokens, codeToValue);

  return (
    <Dialog onClose={closeDialog} open fullWidth>
      <DialogTitle>Formula</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Switch checked={showValues} onChange={(event) => setShowValues(event.target.checked)} size="small" />
          }
          label="Show Values"
          sx={{ mb: 1 }}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {tokens.map((token, index) => {
            const chip = (
              <Chip
                label={
                  token.type === 'variable' && showValues ?
                    formatMetricValue(codeToValue[token.code])
                  : displayValueToken(tokens, index, {})
                }
                color={token.type === 'variable' ? 'primary' : 'default'}
                variant={token.type === 'variable' ? 'filled' : 'outlined'}
              />
            );

            if (token.type !== 'variable') return <span key={token.id}>{chip}</span>;

            return (
              <Tooltip key={token.id} title={`${token.code} = ${codeToValue[token.code] ?? '0'}`}>
                {chip}
              </Tooltip>
            );
          })}
        </Box>
        {summary && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Typography variant="body2">
              {formatNumber(summary.leftValue)} {summary.met ? '' : 'not '}
              {summary.operator} {formatNumber(summary.rightValue)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
